import { motion } from 'framer-motion'
import { Calendar, Star, MoreHorizontal, Eye, CalendarPlus, XCircle } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { getInitials, formatDate } from '@/lib/utils'

interface Application {
  id: string
  stageId: string
  candidate: {
    id: string
    name: string
    email: string
    avatarUrl?: string
    rating?: number
  }
  appliedAt: string
  source?: string
  hasResume?: boolean
  interviewCount?: number
}

interface ApplicationCardProps {
  application: Application
  onClick?: () => void
  isDragging?: boolean
}

export function ApplicationCard({ application, onClick, isDragging }: ApplicationCardProps) {
  const { candidate } = application

  return (
    <motion.div
      layout
      layoutId={application.id}
      onClick={onClick}
      className={`
        p-3 bg-card rounded-lg border border-border cursor-pointer
        hover:border-border/80 hover:shadow-sm transition-all
        ${isDragging ? 'shadow-lg scale-105 rotate-2' : ''}
      `}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-9 w-9">
          <AvatarImage src={candidate.avatarUrl} />
          <AvatarFallback className="text-xs">
            {getInitials(candidate.name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm truncate">{candidate.name}</h4>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation()
                  onClick?.()
                }}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation()
                }}>
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  Schedule Interview
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation()
                }} className="text-destructive">
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-xs text-muted-foreground truncate">{candidate.email}</p>

          <div className="flex items-center gap-2 mt-2">
            {candidate.rating && candidate.rating > 0 && (
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-3 w-3 ${
                      i < candidate.rating!
                        ? 'fill-warning text-warning'
                        : 'text-muted-foreground/30'
                    }`}
                  />
                ))}
              </div>
            )}
            {application.source && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {application.source}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDate(application.appliedAt)}
            </span>
            {application.interviewCount && application.interviewCount > 0 && (
              <span>{application.interviewCount} interview(s)</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
